import { Routes, Route, Navigate, useParams } from "react-router-dom";

import AuthLayout from "layouts/auth";
import AllNewApplicationsView from "views/dashboard/AllNewApplicationsView";
import FollowUpListingView from "views/dashboard/FollowUpListingView";
import DashboardNewApplication from "views/dashboard/DashboardNewApplication";
import DashboardFollowUp from "views/dashboard/DashboardFollowUp";
import { HomeView } from "views/dashboard/HomeView";
import { TrackRecordResultsView } from "views/dashboard/TrackRecordResultsView";
import { DashboardPendingItemsView } from "views/dashboard/DashboardPendingItemsView";
import { DashboardUpdateStatusView } from "views/dashboard/DashboardUpdateStatusView";
import AppSettingsView from "views/dashboard/AppSettingsView";
import ChangePasswordView from "views/dashboard/ChangePasswordView";
import UsersManagementView from "views/dashboard/UsersManagementView";
import NewApplicationPage from "pages/NewApplication";
import ProtectedRoute from "components/ProtectedRoute";
import AllAssignmentsView from "views/dashboard/AllAssignmentsView";
import DashboardAssignment from "views/dashboard/DashboardAssignment";
import AllOppositionsView from "views/dashboard/AllOppositionsView";
import DashboardOpposition from "views/dashboard/DashboardOpposition";
import AllRenewalView from "views/dashboard/AllRenewalView";
import DashboardRenewal from "views/dashboard/DashboardRenewal";
import AllLegalCertificatesView from "views/dashboard/AllLegalCertificatesView";
import DashboardLegalCertificate from "views/dashboard/DashboardLegalCertificate";
import AllSurveillancesView from "views/dashboard/AllSurveillancesView";
import DashboardSurveillance from "views/dashboard/DashboardSurveillance";
import InvoiceReportView from "views/dashboard/InvoiceReportView";
import { useAuthStore, selectIsAuthenticated } from "store/authStore";

function RedirectFilingToDashboard() {
  const { step, leadId } = useParams<{ step: string; leadId: string }>();
  return (
    <Navigate
      to={`/dashboard/applications/new-application/filing/${step || "check-list-of-documents-to-client"}/${leadId || ""}`}
      replace
    />
  );
}

const DashboardNewApplicationRoutes = () => (
  <ProtectedRoute>
    <DashboardNewApplication />
  </ProtectedRoute>
);

function RootRedirect() {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const hasHydrated = useAuthStore((s) => s._hasHydrated);

  if (!hasHydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F7F7F7]">
        <div className="text-sm text-[#6D6D6D]">Loading...</div>
      </div>
    );
  }

  return (
    <Navigate
      to={isAuthenticated ? "/dashboard/home" : "/auth/sign-in"}
      replace
    />
  );
}

const App = () => {
  return (
    <Routes>
      <Route path="auth/*" element={<AuthLayout />} />
      <Route path="dashboard" element={<Navigate to="/dashboard/home" replace />} />
      <Route
        path="dashboard/home"
        element={
          <ProtectedRoute>
            <HomeView />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/home/track-results"
        element={
          <ProtectedRoute>
            <TrackRecordResultsView />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/home/pending-items"
        element={
          <ProtectedRoute>
            <DashboardPendingItemsView />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/home/update-status"
        element={
          <ProtectedRoute>
            <DashboardUpdateStatusView />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/applications"
        element={
          <ProtectedRoute>
            <AllNewApplicationsView />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/follow-ups"
        element={
          <ProtectedRoute>
            <FollowUpListingView />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/follow-ups/new"
        element={<Navigate to="discussion/preliminary-discussion-with-the-client" replace />}
      />
      <Route
        path="dashboard/follow-ups/new/:section/:step"
        element={
          <ProtectedRoute>
            <DashboardFollowUp />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/follow-ups/new/:section/:step/:leadId"
        element={
          <ProtectedRoute>
            <DashboardFollowUp />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/app-settings"
        element={
          <ProtectedRoute>
            <AppSettingsView />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/users"
        element={
          <ProtectedRoute>
            <UsersManagementView />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/change-password"
        element={
          <ProtectedRoute>
            <ChangePasswordView />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/assignments"
        element={
          <ProtectedRoute>
            <AllAssignmentsView />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/assignments/new"
        element={<Navigate to="discussion/preliminary-discussion-with-the-client" replace />}
      />
      <Route
        path="dashboard/assignments/new/:section/:step"
        element={
          <ProtectedRoute>
            <DashboardAssignment />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/assignments/new/:section/:step/:leadId"
        element={
          <ProtectedRoute>
            <DashboardAssignment />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/opposition"
        element={
          <ProtectedRoute>
            <AllOppositionsView />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/opposition/new"
        element={<Navigate to="discussion/preliminary-discussion-with-the-client" replace />}
      />
      <Route
        path="dashboard/opposition/new/:section/:step"
        element={
          <ProtectedRoute>
            <DashboardOpposition />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/opposition/new/:section/:step/:leadId"
        element={
          <ProtectedRoute>
            <DashboardOpposition />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/renewal"
        element={
          <ProtectedRoute>
            <AllRenewalView />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/renewal/new"
        element={<Navigate to="discussion/preliminary-discussion-with-the-client" replace />}
      />
      <Route
        path="dashboard/renewal/new/:section/:step"
        element={
          <ProtectedRoute>
            <DashboardRenewal />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/renewal/new/:section/:step/:leadId"
        element={
          <ProtectedRoute>
            <DashboardRenewal />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/legal-certificate"
        element={
          <ProtectedRoute>
            <AllLegalCertificatesView />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/legal-certificate/new"
        element={<Navigate to="discussion/preliminary-discussion-with-the-client" replace />}
      />
      <Route
        path="dashboard/legal-certificate/new/:section/:step"
        element={
          <ProtectedRoute>
            <DashboardLegalCertificate />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/legal-certificate/new/:section/:step/:leadId"
        element={
          <ProtectedRoute>
            <DashboardLegalCertificate />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/surveillance"
        element={
          <ProtectedRoute>
            <AllSurveillancesView />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/surveillance/new"
        element={<Navigate to="discussion/preliminary-discussion-with-the-client" replace />}
      />
      <Route
        path="dashboard/surveillance/new/:section/:step"
        element={
          <ProtectedRoute>
            <DashboardSurveillance />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/surveillance/new/:section/:step/:leadId"
        element={
          <ProtectedRoute>
            <DashboardSurveillance />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/reports/invoices"
        element={
          <ProtectedRoute>
            <InvoiceReportView />
          </ProtectedRoute>
        }
      />
      <Route
        path="dashboard/applications/new-application"
        element={<Navigate to="discussion/preliminary-discussion-with-the-client" replace />}
      />
      <Route
        path="dashboard/applications/new-application/:section/:step"
        element={<DashboardNewApplicationRoutes />}
      />
      <Route
        path="dashboard/applications/new-application/:section/:step/:leadId"
        element={<DashboardNewApplicationRoutes />}
      />
      <Route
        path="new-application/filing/:step/:leadId"
        element={
          <ProtectedRoute>
            <RedirectFilingToDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="new-application"
        element={
          <ProtectedRoute>
            <NewApplicationPage />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<RootRedirect />} />
    </Routes>
  );
};

export default App;
